
import { defineComponent, reactive, computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Form, Input, Progress } from 'ant-design-vue';
import axios, { AxiosError } from 'axios';

import { compressImage, processApiErrorMessages } from '@/helper/utils';
import { sendPlantPhoto } from '@/api/plant';
import { useStore } from '@/store';
import { amplitudeInstance } from '@/analytics';
import { MODALS, ServerError } from '@/@types/base';

import BaseIcon from '@/components/BaseIcon.vue';

interface FormState {
  search: string;
}

export default defineComponent({
  name: 'SearchForm',

  components: {
    BaseIcon,
    AForm: Form,
    AInput: Input,
    AProgress: Progress,
  },

  props: {
    searchPlant: {
      type: String,
      default: '',
    },
  },

  emits: ['search'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const formState = reactive<FormState>({ search: props.searchPlant });
    const userEmpty = computed<boolean>(() => store.getters['auth/userEmpty']);

    const submitForm = function searchPlantByName() {
      const searchStr = formState.search.trim();

      if (!searchStr) {
        formState.search = searchStr;

        return;
      }

      emit('search', searchStr);
    };

    const uploadPhoto = function showUploadPhotoModal() {
      const modalName = userEmpty.value
        ? MODALS.AUTHORIZATION
        : MODALS.PHOTO_UPLOAD;

      if (modalName === MODALS.AUTHORIZATION) {
        const amplitudeEvents = {
          Home: 'upload-home_page',
          SearchResults: 'upload-search_result_page',
        };

        if (route.name && route.name in amplitudeEvents) {
          const key = route.name as keyof typeof amplitudeEvents;

          amplitudeInstance.logEvent('Auth_screen_shown', {
            placement: amplitudeEvents[key],
          });
        }
      }

      store.dispatch('base/openModal', modalName);
    };

    const isFetch = ref<boolean>(false);
    const loadedPercent = ref<number>(8);

    const animateProgress = function runProgressAnimation() {
      loadedPercent.value = 8;

      const load: ReturnType<typeof setTimeout> = setInterval(() => {
        if (loadedPercent.value >= 88) {
          loadedPercent.value = 99;

          return clearInterval(load);
        }

        loadedPercent.value = loadedPercent.value + 20;
      }, 1000);
    };

    const sendFile = async (event: Event): Promise<void> => {
      const target = event.target as HTMLInputElement;
      const files = target.files as FileList;

      if (!files.length) {
        return;
      }

      const file = files[0];

      isFetch.value = true;

      animateProgress();

      const uploadFile = await compressImage(file);
      const formData = new FormData();

      formData.append('photo', uploadFile);

      try {
        const plantsSuggestion = await sendPlantPhoto(formData);

        store.dispatch('plant/setSearchPlantsResult', plantsSuggestion);

        router.push({ name: 'SearchResults', query: {} });
      } catch (error) {
        store.dispatch('plant/setSearchPlantsResult', []);

        if (axios.isAxiosError(error)) {
          const err = error as AxiosError<ServerError>;

          if (err.response?.status !== 403) {
            if (err.response?.status === 422) {
              processApiErrorMessages(err);
            }

            router.push({ name: 'SearchResults', query: {} });
          }
        }
      }

      isFetch.value = false;
    };

    return {
      t,
      formState,
      userEmpty,
      submitForm,
      uploadPhoto,
      isFetch,
      loadedPercent,
      sendFile,
    };
  },
});
